/**
 * Появление карточек.
 *
 * Анимация здесь служит одному: показать, что список сменился — отфильтровали
 * ленту, пришли новые данные. Поэтому она короткая, со сдвигом в несколько
 * пикселей и без «въездов» через пол-экрана: карточки читают, а не смотрят.
 *
 * При `prefers-reduced-motion` движения нет вовсе — не «поменьше», а нет:
 * у людей с вестибулярными нарушениями от «поменьше» тоже укачивает. За это
 * отвечает gsap.matchMedia: когда условие перестаёт совпадать, созданные в нём
 * анимации откатываются сами.
 */
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import type { RefObject } from 'react';

gsap.registerPlugin(useGSAP);

interface RevealOptions {
  /** Что анимировать внутри области. */
  selector?: string;
  /** Пауза между соседними рядами. */
  stagger?: number;
  /** На сколько пикселей карточка приподнята в начале. */
  y?: number;
}

/**
 * Проявляет элементы внутри области при смене данных.
 *
 * Зависимости — это данные, а не рендер: лента перерисовывается на каждый
 * ввод в поиске, и анимировать её на каждое нажатие клавиши значит мешать
 * читать.
 */
export function useReveal(
  scope: RefObject<HTMLElement>,
  deps: unknown[],
  { selector = '.reveal', stagger = 0.06, y = 10 }: RevealOptions = {},
) {
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(
        {
          motion: '(prefers-reduced-motion: no-preference)',
          reduced: '(prefers-reduced-motion: reduce)',
        },
        (ctx) => {
          if (ctx.conditions?.reduced) return;
          // fromTo, а не from: конечное состояние задано явно. Прерванный на
          // полпути from оставляет карточку невидимой навсегда — а прервать
          // его легко, достаточно сменить фильтр, пока лента ещё проявляется.
          gsap.fromTo(
            selector,
            { autoAlpha: 0, y },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.32,
              // Задержка идёт по рядам, а не по карточкам: соседи в одном ряду
              // обязаны двигаться вместе, иначе в полёте они стоят на разной
              // высоте, и сетка выглядит сломанной. Именно это и ломалось.
              stagger: { each: stagger, grid: 'auto', axis: 'y', from: 'start' },
              ease: 'power2.out',
              overwrite: true,
              clearProps: 'transform',
            },
          );
        },
        scope,
      );
      return () => mm.revert();
    },
    { scope, dependencies: deps, revertOnUpdate: true },
  );
}
